import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";

import { LocalDate, LocalTime } from "js-joda";
import { Dispatch, bindActionCreators } from "redux";

import { Locale } from "@plex/culture-core";
import { DateInput, Form, NumericInput, TextArea, TextInput, TimeInput } from "@plex/react-components";

import { IMessageId } from "infrastructure/localization";
import { useLocalizedValues } from "ui/hooks";
import { displayMessages } from "ui/screens";

import { messageFormatActions } from "../MessageId.actions";
import { TokenFormat, getTokenValues } from "../MessageId.helpers";
import styles from "../MessageId.module.scss";
import { IMessageIdState, IToken } from "../MessageId.state";

interface ITextPreviewSectionProps {
  textPreview: string;
  tokens: IToken[];
  changeTokenPreviewValue: (tokenValue: { index: number; value: any; displayValue: any }) => void;
  setUnexpectedErrorBanner: (messageId: IMessageId) => void;
  clearErrorBanners: () => void;
}

const TextPreviewSection: React.FC<ITextPreviewSectionProps> = ({ textPreview, tokens, changeTokenPreviewValue, setUnexpectedErrorBanner, clearErrorBanners }) => {
  const localizedValues = useLocalizedValues(() => ({
    tokenPreviewHeading: displayMessages.heading("tokenPreview")
  }));

  const [formattedPreview, setFormattedPreview] = useState("");

  useEffect(() => {
    try {
      const locale = new Locale("en-US");
      const tokenValues = getTokenValues(tokens);
      setFormattedPreview(locale.messages.parse(textPreview)(tokenValues));
      clearErrorBanners();
    } catch (e) {
      setUnexpectedErrorBanner(displayMessages.error("messageTextNotParsed"));
    }
  }, [textPreview, tokens, setUnexpectedErrorBanner, clearErrorBanners]);

  const onDateValueChange = useCallback(
    (value: LocalDate | null, index: number) => {
      changeTokenPreviewValue({
        index,
        value: value || LocalDate.now(),
        displayValue: value
      });
    },
    [changeTokenPreviewValue]
  );

  const onTimeValueChange = useCallback(
    (value: LocalTime | null, index: number) => {
      changeTokenPreviewValue({
        index,
        value: value || LocalDate.now(),
        displayValue: value
      });
    },
    [changeTokenPreviewValue]
  );

  const onTokenValueChange = useCallback(
    (value: any, index: number) => {
      changeTokenPreviewValue({ index, value: value, displayValue: value });
    },
    [changeTokenPreviewValue]
  );

  const renderTokenPreviewField = (token: IToken, index: number) => {
    switch (token.type) {
      case TokenFormat.date:
        return <DateInput name={token.name} onValueChange={value => onDateValueChange(value, index)} value={token.displayValue} />;
      case TokenFormat.time:
        return <TimeInput onValueChange={value => onTimeValueChange(value, index)} value={token.displayValue} formatOptions={{ time: "short" }} />;
      case TokenFormat.number:
      case TokenFormat.plural:
      case TokenFormat.selectOrdinal:
        return <NumericInput id={token.id} name={token.name} onValueChange={value => onTokenValueChange(value, index)} numericValue={token.displayValue} />;
      default:
        return <TextInput onValueChange={value => onTokenValueChange(value, index)} id={token.id} name={token.name} value={token.displayValue} />;
    }
  };

  return (
    tokens && (
      <Form.Section title={localizedValues.tokenPreviewHeading}>
        <div className={styles.tokenPreviewFields}>
          {tokens.map((t: IToken, index: number) => {
            return (
              !t.customToken && (
                <Form.Row id={`token-preview-${t.id}`} key={t.name} className={styles.tokenPreviewValue}>
                  <Form.FieldPair labelText={t.name}>{renderTokenPreviewField(t, index)}</Form.FieldPair>
                </Form.Row>
              )
            );
          })}
        </div>
        <Form.Row>
          <TextArea key="previewTextArea" id="previewTextArea" disabled className={styles.tokenPreviewArea} value={formattedPreview} />
        </Form.Row>
      </Form.Section>
    )
  );
};

export default connect(
  (state: { messageFormat: IMessageIdState }) => {
    return {
      textPreview: state.messageFormat.textPreview,
      tokens: state.messageFormat.tokens
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        changeTokenPreviewValue: messageFormatActions.changeTokenPreviewValue,
        setUnexpectedErrorBanner: messageFormatActions.setUnexpectedErrorBanner,
        clearErrorBanners: messageFormatActions.clearErrorBanners
      },
      dispatch
    )
)(TextPreviewSection);
